import { useAuth } from '@/contexts/AuthContext';
import { AUTH_STORAGE_KEY, supabase } from '@common/api/authService';
import { featureService, OwnerTransferStatusResponse } from '@common/api/featureService';
import { useTranslation } from '@client-app/i18n';
import {
    CheckCircle,
    CircleNotch,
    SignOut,
    WarningCircle
} from '@phosphor-icons/react';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const normalizeEmail = (email?: string | null) => String(email || '').trim().toLowerCase();

const ownerTransferStatusCache = new Map<string, OwnerTransferStatusResponse>();

const getCachedOwnerTransferStatus = (token: string) => (
    token ? ownerTransferStatusCache.get(token) || null : null
);

const OwnerTransferAcceptPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const {
        user,
        openSignInModal,
        refreshWorkspaces,
        setCurrentWorkspace,
        switchWorkspace,
    } = useAuth();
    const [status, setStatus] = useState<OwnerTransferStatusResponse | null>(() => getCachedOwnerTransferStatus(token));
    const [isLoading, setIsLoading] = useState(() => !getCachedOwnerTransferStatus(token));
    const [isAccepting, setIsAccepting] = useState(false);
    const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [acceptedWorkspaceId, setAcceptedWorkspaceId] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const loadStatus = async () => {
            if (!token) {
                setError(t('website.ownerTransferAccept.missingToken'));
                setStatus(null);
                setIsLoading(false);
                return;
            }

            const cachedStatus = getCachedOwnerTransferStatus(token);
            setStatus(cachedStatus);
            setIsLoading(!cachedStatus);
            setError(null);
            try {
                const nextStatus = await featureService.getOwnerTransferStatus(token);
                if (isMounted) {
                    ownerTransferStatusCache.set(token, nextStatus);
                    setStatus(nextStatus);
                }
            } catch (err: any) {
                if (isMounted) {
                    setError(err?.message || t('website.ownerTransferAccept.invalidOrExpired'));
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        void loadStatus();

        return () => {
            isMounted = false;
        };
    }, [t, token]);

    const isDifferentAccount = useMemo(() => {
        if (!user || !status?.targetEmail) {
            return false;
        }
        return normalizeEmail(user.email) !== normalizeEmail(status.targetEmail);
    }, [status?.targetEmail, user]);

    const statusReason = String(status?.reasonCode || status?.status || '').toUpperCase();
    const requestExpired = status?.valid === false && statusReason === 'EXPIRED';
    const requestCancelled = status?.valid === false && statusReason === 'CANCELLED';
    const requestAccepted = status?.valid === false && statusReason === 'ACCEPTED';
    const requestUnavailable = status?.valid === false && !requestExpired && !requestCancelled && !requestAccepted;
    const canAccept = Boolean(token && user && status?.valid && !isDifferentAccount && !acceptedWorkspaceId);
    const shouldShowSignOutButton = Boolean(user && !acceptedWorkspaceId);

    const handleAccept = async () => {
        if (!canAccept) {
            return;
        }

        setIsAccepting(true);
        setError(null);
        try {
            const response = await featureService.acceptOwnerTransfer(token);
            const workspaceId = response.workspaceId || status?.workspaceId;
            if (workspaceId) {
                setAcceptedWorkspaceId(workspaceId);
                setCurrentWorkspace(workspaceId);
                try {
                    await refreshWorkspaces({
                        preferredWorkspaceId: workspaceId,
                        silent: true,
                    });
                    switchWorkspace(workspaceId, { replace: true });
                } catch (refreshError) {
                    setError(t('website.ownerTransferAccept.acceptedRefreshFailed'));
                }
            }
        } catch (err: any) {
            setError(err?.message || t('website.ownerTransferAccept.acceptFailed'));
        } finally {
            setIsAccepting(false);
        }
    };

    const handleSwitchAccount = async () => {
        setIsSwitchingAccount(true);
        setError(null);
        const redirectTarget = isDifferentAccount
            ? `/admin/auth?redirect=${encodeURIComponent(window.location.href)}`
            : '/admin/auth';

        Cookies.remove(AUTH_STORAGE_KEY);
        try {
            await supabase.auth.signOut({ scope: 'local' });
        } catch (err) {
            console.error('Failed to sign out before owner transfer account switch:', err);
        } finally {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            window.location.href = redirectTarget;
        }
    };

    const targetLabel = status?.targetEmail || t('website.ownerTransferAccept.anotherAccount');
    const title = acceptedWorkspaceId
        ? t('website.ownerTransferAccept.acceptedTitle')
        : requestExpired
            ? t('website.ownerTransferAccept.expiredTitle')
            : requestCancelled
                ? t('website.ownerTransferAccept.cancelledTitle')
                : requestAccepted
                    ? t('website.ownerTransferAccept.completedTitle')
                    : requestUnavailable
                        ? t('website.ownerTransferAccept.unavailableTitle')
                        : t('website.ownerTransferAccept.acceptTitle', {
                            workspace: status?.workspaceName || t('website.ownerTransferAccept.thisWorkspace'),
                        });
    const inactiveTransferMessage = requestExpired
        ? t('website.ownerTransferAccept.expiredDescription')
        : requestCancelled
            ? t('website.ownerTransferAccept.cancelledDescription')
            : requestAccepted
                ? t('website.ownerTransferAccept.completedDescription')
                : t('website.ownerTransferAccept.unavailableDescription');

    return (
        <div className="flex min-h-screen w-full items-center justify-center bg-[#F8F9FB] px-4 py-10">
            {shouldShowSignOutButton ? (
                <button
                    onClick={() => { void handleSwitchAccount(); }}
                    disabled={isSwitchingAccount}
                    className="fixed top-6 right-6 z-[101] flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 shadow-sm transition-all hover:bg-gray-50 disabled:cursor-wait disabled:opacity-70"
                >
                    {isSwitchingAccount ? (
                        <CircleNotch size={16} className="animate-spin text-[#1A162F]" weight="bold" />
                    ) : (
                        <SignOut size={16} className="text-[#1A162F]" weight="bold" />
                    )}
                    <span className="text-[13px] font-bold text-[#1A162F]">
                        {isSwitchingAccount
                            ? (isDifferentAccount ? t('website.ownerTransferAccept.signingOut') : t('website.ownerTransferAccept.loggingOut'))
                            : (isDifferentAccount ? t('website.ownerTransferAccept.signOutAndSwitch') : t('website.ownerTransferAccept.logOut'))}
                    </span>
                </button>
            ) : null}

            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gray-200">
                        {acceptedWorkspaceId ? (
                            <CheckCircle size={28} weight="fill" className="text-green-600" />
                        ) : (
                            <span className="text-base font-semibold text-[#1A162F]">
                                {(status?.workspaceName || 'W').charAt(0).toUpperCase()}
                            </span>
                        )}
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-[#1A162F]">{title}</h1>
                    <p className="text-sm leading-6 text-[#666F8D]">
                        {t('website.ownerTransferAccept.invitedDescription', {
                            owner: status?.previousOwnerName || t('website.ownerTransferAccept.currentOwnerFallback'),
                            workspace: status?.workspaceName || t('website.ownerTransferAccept.thisWorkspace'),
                        })}
                    </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    {isLoading ? (
                        <div className="flex items-center justify-center gap-2 py-8 text-sm text-[#666F8D]">
                            <CircleNotch size={18} className="animate-spin" weight="bold" />
                            {t('website.ownerTransferAccept.loading')}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {error ? (
                                <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    <WarningCircle size={18} weight="bold" className="mt-0.5 shrink-0" />
                                    <span>{error}</span>
                                </div>
                            ) : null}

                            {isDifferentAccount ? (
                                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                    <WarningCircle size={18} weight="bold" className="mt-0.5 shrink-0" />
                                    <span>
                                        {t('website.ownerTransferAccept.differentAccountDescription', { email: targetLabel })}
                                    </span>
                                </div>
                            ) : null}

                            {status?.valid === false ? (
                                <div className="rounded-lg border border-gray-200 bg-[#F8F9FC] p-3 text-sm text-[#666F8D]">
                                    {inactiveTransferMessage}
                                </div>
                            ) : null}

                            <div className="rounded-lg border border-[#E8EBF3] bg-[#FCFCFC] p-3">
                                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#8A93B2]">
                                    {t('website.ownerTransferAccept.newOwner')}
                                </p>
                                <p className="mt-1 text-sm font-medium text-[#1A162F]">
                                    {status?.targetName || status?.targetEmail || t('website.ownerTransferAccept.pendingUser')}
                                </p>
                                {status?.targetEmail ? (
                                    <p className="mt-0.5 text-xs text-[#666F8D]">{status.targetEmail}</p>
                                ) : null}
                            </div>

                            {isDifferentAccount ? null : status?.valid === false ? null : !user ? (
                                <button
                                    onClick={openSignInModal}
                                    className="w-full rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E]"
                                >
                                    {t('website.ownerTransferAccept.signInToContinue')}
                                </button>
                            ) : (
                                <button
                                    onClick={() => { void handleAccept(); }}
                                    disabled={!canAccept || isAccepting}
                                    className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isAccepting ? (
                                        <>
                                            <CircleNotch size={16} className="animate-spin" weight="bold" />
                                            {t('website.ownerTransferAccept.accepting')}
                                        </>
                                    ) : (
                                        t('website.ownerTransferAccept.acceptOwnership')
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OwnerTransferAcceptPage;
